import { useState, useCallback, useRef, useMemo } from 'react';
import type { InventoryItem, EquipmentSlotKey, SlotLocation } from '../types';
import { locationsEqual } from '../utils/location';
import {
  NEARBY_ITEMS, CAR_TRUNK_ITEMS, INVENTORY_ITEMS, BACKPACK_ITEMS,
  FAST_ITEMS, CAR_PLATE, PLAYER, CAPACITIES,
} from '../data';
import { SLOT_EQUIPMENT_TYPE } from '../equipmentConfig';

function removeFromSlots(
  slots: (InventoryItem | null)[],
  id: string,
): (InventoryItem | null)[] {
  return slots.map(s => (s?.id === id ? null : s));
}

function addToSlots(
  slots: (InventoryItem | null)[],
  item: InventoryItem,
): (InventoryItem | null)[] {
  const next = [...slots];
  const idx = next.findIndex(s => s === null);
  if (idx !== -1) next[idx] = item;
  return next;
}

function reduceQuantity(
  slots: (InventoryItem | null)[],
  id: string,
  qty: number,
): (InventoryItem | null)[] {
  return slots.map(s => {
    if (s?.id !== id) return s;
    return s.quantity > qty ? { ...s, quantity: s.quantity - qty } : null;
  });
}

type ArraySetter = React.Dispatch<React.SetStateAction<(InventoryItem | null)[]>>;

export function useInventory() {
  const [nearbyItems, setNearbyItems] = useState<(InventoryItem | null)[]>(NEARBY_ITEMS);
  const [carTrunk, setCarTrunk]       = useState<(InventoryItem | null)[]>(CAR_TRUNK_ITEMS);
  const [inventory, setInventory]     = useState<(InventoryItem | null)[]>(INVENTORY_ITEMS);
  const [backpack, setBackpack]       = useState<(InventoryItem | null)[]>(BACKPACK_ITEMS);
  const [fastItems, setFastItems]     = useState<(InventoryItem | null)[]>(FAST_ITEMS);
  const [equipment, setEquipment]     = useState<Partial<Record<EquipmentSlotKey, InventoryItem>>>({});
  const [selectedLocation, setSelectedLocation] = useState<SlotLocation | null>(null);

  const stateRef = useRef({ nearbyItems, carTrunk, inventory, backpack, fastItems, equipment });
  stateRef.current = { nearbyItems, carTrunk, inventory, backpack, fastItems, equipment };

  const selectItem = useCallback((location: SlotLocation | null) => {
    setSelectedLocation(prev =>
      prev !== null && location !== null && locationsEqual(prev, location) ? null : location
    );
  }, []);

  const consumeItem = useCallback((item: InventoryItem, qty: number) => {
    const { id } = item;
    const reduce = (s: (InventoryItem | null)[]) => reduceQuantity(s, id, qty);
    setInventory(reduce);
    setBackpack(reduce);
    setNearbyItems(reduce);
    setCarTrunk(reduce);
    setFastItems(reduce);
    setSelectedLocation(null);
  }, []);

  const dropItem = useCallback((item: InventoryItem, qty: number) => {
    const { id } = item;
    if (qty >= item.quantity) {
      setInventory(s  => removeFromSlots(s, id));
      setBackpack(s   => removeFromSlots(s, id));
      setFastItems(s  => removeFromSlots(s, id));
      setCarTrunk(s   => removeFromSlots(s, id));
      setEquipment(s => {
        const next = { ...s };
        (Object.keys(next) as EquipmentSlotKey[]).forEach(k => {
          if (next[k]?.id === id) delete next[k];
        });
        return next;
      });
      setNearbyItems(s => addToSlots(removeFromSlots(s, id), item));
    } else {
      const reduce = (s: (InventoryItem | null)[]) => reduceQuantity(s, id, qty);
      setInventory(reduce);
      setBackpack(reduce);
      setFastItems(reduce);
      setCarTrunk(reduce);
      setEquipment(s => {
        const next = { ...s };
        (Object.keys(next) as EquipmentSlotKey[]).forEach(k => {
          const slot = next[k];
          if (slot?.id === id) next[k] = { ...slot, quantity: slot.quantity - qty };
        });
        return next;
      });
      const partial: InventoryItem = { ...item, id: `${id}_${Date.now()}`, quantity: qty };
      setNearbyItems(s => addToSlots(s, partial));
    }
    setSelectedLocation(null);
  }, []);

  const takeItem = useCallback((item: InventoryItem, qty: number) => {
    const { id } = item;
    if (qty >= item.quantity) {
      setNearbyItems(s => removeFromSlots(s, id));
      setCarTrunk(s   => removeFromSlots(s, id));
      setInventory(s  => addToSlots(s, item));
    } else {
      const reduce = (s: (InventoryItem | null)[]) => reduceQuantity(s, id, qty);
      setNearbyItems(reduce);
      setCarTrunk(reduce);
      const partial: InventoryItem = { ...item, id: `${id}_${Date.now()}`, quantity: qty };
      setInventory(s => addToSlots(s, partial));
    }
    setSelectedLocation(null);
  }, []);

  const moveItem = useCallback((from: SlotLocation, to: SlotLocation) => {
    const state = stateRef.current;

    const getArrayItems = (section: string): (InventoryItem | null)[] => {
      switch (section) {
        case 'nearby':    return state.nearbyItems;
        case 'carTrunk':  return state.carTrunk;
        case 'inventory': return state.inventory;
        case 'backpack':  return state.backpack;
        case 'fastItems': return state.fastItems;
        default:          return [];
      }
    };

    const getSetter = (section: string): ArraySetter | undefined => {
      switch (section) {
        case 'nearby':    return setNearbyItems;
        case 'carTrunk':  return setCarTrunk;
        case 'inventory': return setInventory;
        case 'backpack':  return setBackpack;
        case 'fastItems': return setFastItems;
      }
    };

    // Get items at source and destination
    const fromItem: InventoryItem | null =
      from.section === 'equipment'
        ? state.equipment[from.key] ?? null
        : getArrayItems(from.section)[from.index] ?? null;

    if (!fromItem) return;

    const toItem: InventoryItem | null =
      to.section === 'equipment'
        ? state.equipment[to.key] ?? null
        : getArrayItems(to.section)[to.index] ?? null;

    // Equipment slots only accept items with the matching equipmentType
    if (to.section === 'equipment') {
      if (fromItem.equipmentType !== SLOT_EQUIPMENT_TYPE[to.key]) return;
    }
    // If swapping out of an equipment slot back into an equipment slot, validate there too
    if (from.section === 'equipment' && to.section === 'equipment' && toItem) {
      if (toItem.equipmentType !== SLOT_EQUIPMENT_TYPE[from.key]) return;
    }

    // Same-section array swap — one atomic update
    if (
      from.section !== 'equipment' &&
      to.section !== 'equipment' &&
      from.section === to.section
    ) {
      const setter = getSetter(from.section)!;
      setter(prev => {
        const next = [...prev];
        next[(from as { section: string; index: number }).index] = toItem;
        next[(to as { section: string; index: number }).index] = fromItem;
        return next;
      });
      return;
    }

    // Update source slot
    if (from.section === 'equipment') {
      if (toItem) {
        setEquipment(prev => ({ ...prev, [from.key]: toItem }));
      } else {
        setEquipment(prev => {
          const next = { ...prev };
          delete next[from.key];
          return next;
        });
      }
    } else {
      const setter = getSetter(from.section)!;
      const fromIndex = (from as { section: string; index: number }).index;
      setter(prev => {
        const next = [...prev];
        next[fromIndex] = toItem;
        return next;
      });
    }

    // Update destination slot
    if (to.section === 'equipment') {
      setEquipment(prev => ({ ...prev, [to.key]: fromItem }));
    } else {
      const setter = getSetter(to.section)!;
      const toIndex = (to as { section: string; index: number }).index;
      setter(prev => {
        const next = [...prev];
        next[toIndex] = fromItem;
        return next;
      });
    }
  }, []);

  const splitItem = useCallback((item: InventoryItem, location: SlotLocation, qty: number) => {
    if (qty <= 0 || qty >= item.quantity || location.section === 'equipment') return;
    const { id } = item;
    const partial: InventoryItem = { ...item, id: `${id}_${Date.now()}`, quantity: qty };
    const setters: Partial<Record<string, ArraySetter>> = {
      nearby:    setNearbyItems,
      carTrunk:  setCarTrunk,
      inventory: setInventory,
      backpack:  setBackpack,
      fastItems: setFastItems,
    };
    const setter = setters[location.section];
    if (!setter) return;
    setter(s => {
      const reduced = reduceQuantity(s, id, qty);
      const hasSpace = reduced.some(slot => slot === null);
      if (!hasSpace) return s;
      return addToSlots(reduced, partial);
    });
  }, []);

  const canDrop = useCallback((item: InventoryItem, _from: SlotLocation, to: SlotLocation): boolean => {
    if (to.section === 'equipment') {
      return item.equipmentType === SLOT_EQUIPMENT_TYPE[to.key];
    }
    return true;
  }, []);

  const selectedItem = useMemo<InventoryItem | null>(() => {
    if (!selectedLocation) return null;
    const s = stateRef.current;
    if (selectedLocation.section === 'equipment') return s.equipment[selectedLocation.key] ?? null;
    const arrays: Record<string, (InventoryItem | null)[]> = {
      nearby:    s.nearbyItems,
      carTrunk:  s.carTrunk,
      inventory: s.inventory,
      backpack:  s.backpack,
      fastItems: s.fastItems,
    };
    return arrays[selectedLocation.section]?.[selectedLocation.index] ?? null;
  }, [selectedLocation, nearbyItems, carTrunk, inventory, backpack, fastItems, equipment]);

  return {
    nearbyItems,
    carTrunk,
    inventory,
    backpack,
    fastItems,
    equipment,
    capacities: CAPACITIES,
    carPlate: CAR_PLATE,
    player: PLAYER,
    selectedLocation,
    selectedItem,
    selectItem,
    consumeItem,
    dropItem,
    takeItem,
    splitItem,
    moveItem,
    canDrop,
  };
}
